import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/ContactModal.css'; // Reuse the same styling

const BookingModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    date: '',
    name: '',
    passengerName: '',
    phone: '',
    email: '',
    mobilityAid: 'None',
    passengers: 0,
    pickupTime: '',
    pickupStreet: '',
    pickupCity: '',
    pickupState: 'WA',
    pickupZip: '',
    dropoffTime: '',
    dropoffStreet: '',
    dropoffCity: '',
    dropoffState: 'WA',
    dropoffZip: '',
    tripNotes: '',
    hasReturnTrip: false,
    returnDate: '',
    returnPickupTime: '',
    returnPickupStreet: '',
    returnPickupCity: '',
    returnPickupState: 'WA',
    returnPickupZip: '',
    returnDropoffTime: '',
    returnDropoffStreet: '',
    returnDropoffCity: '',
    returnDropoffState: 'WA',
    returnDropoffZip: '',
    returnDropoffSameAsPickup: false,
    returnPassengers: 0
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseInt(value, 10) || 0
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const formDataToSend = {
      formType: 'other',
      ...formData
    };

    try {
      console.log('Submitting booking form:', {
        formType: formDataToSend.formType,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });

      const response = await fetch('https://bxf2u2w062.execute-api.us-west-2.amazonaws.com/prod/booking-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formDataToSend)
      });

      console.log('Response received:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        timestamp: new Date().toISOString()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit booking');
      }

      // Set success status immediately
      setSubmitStatus('success');
      setIsSubmitting(false);
      
      // Keep the success popup visible for 5 seconds, then close modal
      setTimeout(() => {
        setSubmitStatus(null); // First clear success status
        onClose(); // Then close the modal
        // Reset form state
        setTimeout(() => {
          setFormData({
            date: '',
            name: '',
            passengerName: '',
            phone: '',
            email: '',
            mobilityAid: 'None',
            passengers: 0,
            pickupTime: '',
            pickupStreet: '',
            pickupCity: '',
            pickupState: 'WA',
            pickupZip: '',
            dropoffTime: '',
            dropoffStreet: '',
            dropoffCity: '',
            dropoffState: 'WA',
            dropoffZip: '',
            tripNotes: '',
            hasReturnTrip: false,
            returnDate: '',
            returnPickupTime: '',
            returnPickupStreet: '',
            returnPickupCity: '',
            returnPickupState: 'WA',
            returnPickupZip: '',
            returnDropoffTime: '',
            returnDropoffStreet: '',
            returnDropoffCity: '',
            returnDropoffState: 'WA',
            returnDropoffZip: '',
            returnDropoffSameAsPickup: false,
            returnPassengers: 0
          });
        }, 300); // Small delay after closing the modal
      }, 5000);
    } catch (error) {
      // Log detailed error information
      console.error('Detailed booking submission error:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        formType: formDataToSend.formType,
        formDataKeys: Object.keys(formDataToSend)
      });
      
      setSubmitStatus('error');
      setIsSubmitting(false);
    }
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="modal-content"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '600px' }}
          >
            <button className="modal-close" onClick={onClose}>×</button>
            
            <h2>Request a Quote</h2>
            <p className="modal-subtitle">Please provide your journey details</p>
                
                <form onSubmit={handleSubmit} className="contact-form">
                  {/* Common Fields - Top Row */}
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="date">Date*</label>
                      <input
                        type="date"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="pickupTime">Requested Pickup Time*</label>
                      <input
                        type="time"
                        id="pickupTime"
                        name="pickupTime"
                        value={formData.pickupTime}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  
                  {/* Personal Information */}
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Name*</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your Full Name"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="passengerName">Passenger Name*</label>
                      <input
                        type="text"
                        id="passengerName"
                        name="passengerName"
                        value={formData.passengerName}
                        onChange={handleChange}
                        required
                        placeholder="Passenger Full Name"
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Phone*</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="(123) 456-7890"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="email">Email*</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="mobilityAid">Mobility Aids</label>
                      <select
                        id="mobilityAid"
                        name="mobilityAid"
                        value={formData.mobilityAid}
                        onChange={handleChange}
                      >
                        <option value="None">None</option>
                        <option value="Walker">Walker</option>
                        <option value="Wheelchair">Wheelchair</option>
                        <option value="Cane">Cane</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="passengers">Additional Passengers*</label>
                      <input
                        type="number"
                        id="passengers"
                        name="passengers"
                        value={formData.passengers}
                        onChange={handleChange}
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="form-group address-group">
                    <label>Pickup Address*</label>
                    <div className="address-inputs">
                      <input
                        type="text"
                        name="pickupStreet"
                        value={formData.pickupStreet}
                        onChange={handleChange}
                        required
                        placeholder="Street Address"
                      />
                      <div className="address-row">
                        <input
                          type="text"
                          name="pickupCity"
                          value={formData.pickupCity}
                          onChange={handleChange}
                          required
                          placeholder="City"
                        />
                        <select
                          name="pickupState"
                          value={formData.pickupState}
                          onChange={handleChange}
                          required
                        >
                          <option value="WA">WA</option>
                          <option value="OR">OR</option>
                          <option value="ID">ID</option>
                          <option value="CA">CA</option>
                          <option value="AK">AK</option>
                        </select>
                        <input
                          type="text"
                          name="pickupZip"
                          value={formData.pickupZip}
                          onChange={handleChange}
                          required
                          placeholder="ZIP"
                          pattern="[0-9]{5}"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="dropoffTime">Dropoff/Appointment Time*</label>
                      <input
                        type="time"
                        id="dropoffTime"
                        name="dropoffTime"
                        value={formData.dropoffTime}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="form-group address-group">
                    <label>Dropoff Address*</label>
                    <div className="address-inputs">
                      <input
                        type="text"
                        name="dropoffStreet"
                        value={formData.dropoffStreet}
                        onChange={handleChange}
                        required
                        placeholder="Street Address"
                      />
                      <div className="address-row">
                        <input
                          type="text"
                          name="dropoffCity"
                          value={formData.dropoffCity}
                          onChange={handleChange}
                          required
                          placeholder="City"
                        />
                        <select
                          name="dropoffState"
                          value={formData.dropoffState}
                          onChange={handleChange}
                          required
                        >
                          <option value="WA">WA</option>
                          <option value="OR">OR</option>
                          <option value="ID">ID</option>
                          <option value="CA">CA</option>
                          <option value="AK">AK</option>
                        </select>
                        <input
                          type="text"
                          name="dropoffZip"
                          value={formData.dropoffZip}
                          onChange={handleChange}
                          required
                          placeholder="ZIP"
                          pattern="[0-9]{5}"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="tripNotes">Additional Notes</label>
                    <textarea
                      id="tripNotes"
                      name="tripNotes"
                      value={formData.tripNotes}
                      onChange={handleChange}
                      placeholder="Any additional information about your trip or special requirements"
                      rows="3"
                    />
                  </div>
                  
                  {/* Return Trip Checkbox */}
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="hasReturnTrip"
                        checked={formData.hasReturnTrip}
                        onChange={handleChange}
                      />
                      <span>I need a return trip</span>
                    </label>
                  </div>
                  
                  {/* Return Trip Fields */}
                  {formData.hasReturnTrip && (
                    <div className="return-trip-section">
                      <h3>Return Trip Details</h3>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="returnDate">Return Date*</label>
                          <input
                            type="date"
                            id="returnDate"
                            name="returnDate"
                            value={formData.returnDate}
                            onChange={handleChange}
                            required={formData.hasReturnTrip}
                          />
                        </div>
                      
                        <div className="form-group">
                          <label htmlFor="returnPickupTime">Return Pickup Time*</label>
                          <input
                            type="time"
                            id="returnPickupTime"
                            name="returnPickupTime"
                            value={formData.returnPickupTime}
                            onChange={handleChange}
                            required={formData.hasReturnTrip}
                          />
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="returnPassengers">Additional Passengers*</label>
                        <input
                          type="number"
                          id="returnPassengers"
                          name="returnPassengers"
                          value={formData.returnPassengers}
                          onChange={handleChange}
                          min="0"
                          required={formData.hasReturnTrip}
                        />
                      </div>

                      <div className="form-group address-group">
                        <label>Return Pickup Address*</label>
                        <div className="address-inputs">
                          <input
                            type="text"
                            name="returnPickupStreet"
                            value={formData.returnPickupStreet}
                            onChange={handleChange}
                            required={formData.hasReturnTrip}
                            placeholder="Street Address"
                          />
                          <div className="address-row">
                            <input
                              type="text"
                              name="returnPickupCity"
                              value={formData.returnPickupCity}
                              onChange={handleChange}
                              required={formData.hasReturnTrip}
                              placeholder="City"
                            />
                            <select
                              name="returnPickupState"
                              value={formData.returnPickupState}
                              onChange={handleChange}
                              required={formData.hasReturnTrip}
                            >
                              <option value="WA">WA</option>
                              <option value="OR">OR</option>
                              <option value="ID">ID</option>
                              <option value="CA">CA</option>
                              <option value="AK">AK</option>
                            </select>
                            <input
                              type="text"
                              name="returnPickupZip"
                              value={formData.returnPickupZip}
                              onChange={handleChange}
                              required={formData.hasReturnTrip}
                              placeholder="ZIP"
                              pattern="[0-9]{5}"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="returnDropoffTime">Return Dropoff Time*</label>
                          <input
                            type="time"
                            id="returnDropoffTime"
                            name="returnDropoffTime"
                            value={formData.returnDropoffTime}
                            onChange={handleChange}
                            required={formData.hasReturnTrip}
                          />
                        </div>
                      </div>
                      
                      <div className="form-group address-group">
                        <label>Return Dropoff Address*</label>
                        <div className="address-inputs">
                          <input
                            type="text"
                            name="returnDropoffStreet"
                            value={formData.returnDropoffStreet}
                            onChange={handleChange}
                            required={formData.hasReturnTrip}
                            placeholder="Street Address"
                          />
                          <div className="address-row">
                            <input
                              type="text"
                              name="returnDropoffCity"
                              value={formData.returnDropoffCity}
                              onChange={handleChange}
                              required={formData.hasReturnTrip}
                              placeholder="City"
                            />
                            <select
                              name="returnDropoffState"
                              value={formData.returnDropoffState}
                              onChange={handleChange}
                              required={formData.hasReturnTrip}
                            >
                              <option value="WA">WA</option>
                              <option value="OR">OR</option>
                              <option value="ID">ID</option>
                              <option value="CA">CA</option>
                              <option value="AK">AK</option>
                            </select>
                            <input
                              type="text"
                              name="returnDropoffZip"
                              value={formData.returnDropoffZip}
                              onChange={handleChange}
                              required={formData.hasReturnTrip}
                              placeholder="ZIP"
                              pattern="[0-9]{5}"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                  
                  {submitStatus === 'error' && (
                    <div className="status-message error">
                      Failed to submit request. Please try again.
                    </div>
                  )}
                </form>
          </motion.div>
        </motion.div>
      )}
      
      {/* Success popup as a separate modal overlay */}
      {submitStatus === 'success' && (
        <div className="success-overlay">
          <div className="success-popup">
            <div className="success-icon">✓</div>
            <h3>Quote Request Submitted!</h3>
            <p>Thank you for your request. We'll review your details and contact you with pricing information.</p>
            <p className="response-time">Please allow 24 hours for a response (Monday-Friday).</p>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default BookingModal; 